// config/res/modules/async-storage-web.ts
type Pair = [string, string];

const hasLocalStorage =
  typeof window !== "undefined" && !!window.localStorage;

const memory = new Map<string, string>();

const storage = {
  getItem(key: string) {
    return hasLocalStorage ? window.localStorage.getItem(key) : memory.get(key) ?? null;
  },
  setItem(key: string, value: string) {
    if (hasLocalStorage) window.localStorage.setItem(key, value);
    else memory.set(key, value);
  },
  removeItem(key: string) {
    if (hasLocalStorage) window.localStorage.removeItem(key);
    else memory.delete(key);
  },
  clear() {
    if (hasLocalStorage) window.localStorage.clear();
    else memory.clear();
  },
  key(index: number) {
    if (hasLocalStorage) return window.localStorage.key(index);
    return Array.from(memory.keys())[index] ?? null;
  },
  get length() {
    return hasLocalStorage ? window.localStorage.length : memory.size;
  },
};

const AsyncStorage = {
  // API compatível com @react-native-async-storage/async-storage
  getItem: async (key: string): Promise<string | null> => storage.getItem(key),
  setItem: async (key: string, value: string): Promise<void> => { storage.setItem(key, value); },
  removeItem: async (key: string): Promise<void> => { storage.removeItem(key); },
  clear: async (): Promise<void> => { storage.clear(); },
  getAllKeys: async (): Promise<string[]> => {
    if (hasLocalStorage) {
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k) keys.push(k);
      }
      return keys;
    }
    return Array.from(memory.keys());
  },
  multiGet: async (keys: string[]): Promise<Pair[]> =>
    keys.map((k) => [k, storage.getItem(k)] as Pair),
  multiSet: async (pairs: Pair[]): Promise<void> => {
    pairs.forEach(([k, v]) => storage.setItem(k, v));
  },
  multiRemove: async (keys: string[]): Promise<void> => {
    keys.forEach((k) => storage.removeItem(k));
  },
};

export default AsyncStorage;
export { AsyncStorage };
